import { defineStore } from "pinia";
import Swal from 'sweetalert2';
import _ from "lodash";
import dayjs from "dayjs"
import { JobAction } from "~/interface/Job";
import JobBuilder from "~/class/JobBuilderV1";
import { doc, setDoc, deleteField } from "firebase/firestore";
import { set } from "firebase/database";
import { getPendingActivities, syncPendingActivities } from "~/plugins/job.to.activity.plugin";
import { registerResource } from "~/services/repos/versionSync";
import { clearOfflineRuntime } from "~/services/storage/offlineBuckets";

export type RunMode = "cashier" | "terminal" | "readonly" | "2nd_cashier" | null;


export const usePosShopStore = defineStore("shop", {
  state: () => ({
    storeId: "",
    data: {
      "Id": 0,
      "Logo": null,
      "Name": "",
      "CreateDate": "",
      "ExpireDate": "",
      "Reference": null,
      "PromotionCode": "",
      "IsTrial": false,
      "Remarks": null,
      "PackagePlan": ""
    },
    is2ndCashier: false,
    runMode: null as RunMode,
    closeStateCompleted: {
      refreshReceipts: false,
      syncData: false,
      sendEmailDaily: false,
      sendSmsDaily: false,
      closingBatch: false,
    },
  }),
  getters: {
    getStoreId: (state) => state.storeId,
    getRunMode: (state) => state.runMode,
    // Add-only / view-only devices: cannot edit orders or billing, and cannot
    // see shop-sales data. `terminal` and `readonly` are treated identically for
    // these restrictions (side actions, billing settings, dashboard sales tabs).
    isRestrictedMode: (state) =>
      state.runMode === "terminal" || state.runMode === "readonly",
  },
  actions: {
    /**
     * Register `customerInfo` with versionSync. Bootstrap must call this
     * BEFORE `syncAllVersions()` so the resource participates in the
     * version reconcile pass. Kept separate from `loadRunMode()` because that
     * runs after syncAllVersions to wire up runMode.
     */
    init() {
      registerResource({
        resource: "customerInfo",
        refetch: () => useDataservice().customers.fetch(),
        applyToStore: (data: any) => { if (data) this.data = data },
      })
    },

    async loadRunMode() {
      // run mode
      const savedRunMode = localStorage.getItem(`runMode_${this.storeId}`);
      if (
        savedRunMode === "cashier" ||
        savedRunMode === "terminal" ||
        savedRunMode === "readonly" ||
        savedRunMode === "2nd_cashier"
      ) {
        this.runMode = savedRunMode;
        return;
      }

      this.runMode = null;
    },
    setRunMode(mode: RunMode) {
      this.runMode = mode;
      if (mode == null) {
        localStorage.removeItem(`runMode_${this.storeId}`);
        return;
      }

      localStorage.setItem(`runMode_${this.storeId}`, mode);
    },
    clearCashierModeBecauseOwnershipLost() {
      if (this.runMode !== "cashier" && this.runMode !== "2nd_cashier") {
        return;
      }

      this.runMode = null;
      localStorage.removeItem(`runMode_${this.storeId}`);
    },
    async checkIs2ndCashier() {
      try {
        const URL = "http://localhost:9090/api/2ndcashier";
        const response = await fetch(URL);
        const data = await response.json();
        this.is2ndCashier = data.is2ndCashier || false;
      } catch (error) {
        console.log(error);
        this.is2ndCashier = false;
      }
    },

    async openShop(mode: string, { cashInitalAmount = 0, businessDate: businessDateOverride = null }: { cashInitalAmount?: number; businessDate?: string | null } = {}) {
      if (mode == "cashier") {
        const businessModeRaw = useSettingsStore().getSystemEnvByName("BusinessMode")?.Value || "0";
        const businessMode = Number(businessModeRaw) || 0;
        const now = dayjs();
        // Caller (open-shop page, near the cutoff) may pin this session to a chosen
        // business day; otherwise derive it from the current time as before.
        const businessDate = businessDateOverride
          ? businessDateOverride
          : businessMode == 1
            ? now.add(-7, 'hour').format('YYYY-MM-DD')
            : now.format('YYYY-MM-DD');

        const job = new JobBuilder()
          .withCashInitalAmount(cashInitalAmount)
          .withJobIdAndTimestamp(useServerTime().nowISO())
          .withActionType(JobAction.OPEN_SHOP)
          .withCustom({ businessDate })
          .build();

        usePosJobsStore().add(job, true);

        try {
          const rootRef = useFirestore().getRootDataRef();
          await setDoc(
            rootRef,
            {
              status: "opened",
              opened: now.toISOString(),
              closed: deleteField(),
              businessMode,
              live: {
                businessMode,
                businessDate,
              },
            },
            { merge: true },
          );
        } catch (e) {
          console.error("openShop: failed to update customer doc", e);
        }
      }
    },

    async closeShop(payload) {
      // validate: only the main cashier terminal can close the shop
      // (2nd_cashier / terminal / readonly are blocked)
      if (this.runMode !== 'cashier') {
        const { $i18n } = useNuxtApp() as any
        await Swal.fire({
          icon: 'warning',
          title: $i18n?.t('closeShop.secondCashierBlock.title') ?? 'ไม่สามารถปิดร้านได้',
          text: $i18n?.t('closeShop.secondCashierBlock.text') ?? 'ปิดร้านได้เฉพาะเครื่องแคชเชียร์หลักเท่านั้น',
        })
        return
      }

      // validate: all tables must be cleared before closing the shop
      const openTables = usePosJobsStore().tables || {}
      const openTableCount = Object.keys(openTables).length
      if (openTableCount > 0) {
        await Swal.fire({
          icon: 'warning',
          title: 'มีโต๊ะที่ยังไม่ได้ปิด',
          text: `กรุณาเคลียร์โต๊ะให้หมดก่อนปิดร้าน (เหลืออยู่ ${openTableCount} โต๊ะ)`,
        })
        return
      }

      this.resetCloseStateCompleted()

      // Step 1: refresh ALL receipts from the server for this business day so the
      // close-shop summary includes sales from every cashier / 2nd cashier — not
      // just this terminal's stale in-memory copy. The receipts store is already
      // initialized at boot, so init() short-circuits (isInitialized guard) and
      // would NOT actually refetch; use refreshFromServer() instead.
      //
      // Order matters: PUSH this terminal's pending receipts (+ queued voids) to
      // the cloud FIRST, then refetch — so the server set we pull back is complete
      // and current. If the server is unreachable we still let the shop close, but
      // flag the report as local-only so the owner knows the total may be incomplete.
      let receiptsLocalOnly = false
      try {
        const businessDate = usePosJobsStore().getBusinessDate()
        // Sync this terminal's pending receipts/voids to the server before refetching.
        await usePosReceiptsStore().flushOfflineMutations()
        await usePosReceiptsStore().refreshFromServer(businessDate, businessDate)
      } catch (e) {
        receiptsLocalOnly = true
        console.error("close-shop receipts sync/refresh from server failed — printing LOCAL ONLY:", e)
      }
      await new Promise((resolve) => _.delay(resolve, 1000));
      this.closeStateCompleted.refreshReceipts = !receiptsLocalOnly

      // Thermal print report (side effect, not tracked as a checklist step)
      try {
        await this.printReport(payload, { localOnly: receiptsLocalOnly })
      } catch (e) {
        console.error("printReport failed, continuing:", e)
      }

      // Step 2: sync data (addCloseJob + jobs/receipts/activities)
      await this.addCloseJobCard()
      try {
        await this.syncOfflineDataOrThrow()
      } catch (error) {
        console.error("Failed to sync close-shop data before cleanup:", error)
        Swal.fire({ icon: 'error', title: "บันทึกข้อมูลปิดร้านขึ้น cloud ยังไม่ครบ ระบบยังไม่ล้างข้อมูล กรุณาลองปิดร้านอีกครั้ง" })
        return
      }
      this.closeStateCompleted.syncData = true

      await new Promise((resolve) => _.delay(resolve, 1000));

      // Step 3 & 4: email + SMS (each sets its own flag internally)
      await this.sendEmailDaily()
      await this.sendSmsDaily()

      // Step 5: closing batch (updateCloseSummary + clearCloudData)
      await this.updateCloseSummary()
      await this.clearCloudData()
      this.closeStateCompleted.closingBatch = true

      await Swal.fire({ icon: 'success', title: "ปิดร้านเรียบร้อย / Closing Complete", confirmButtonText: "OK" });
      window.location.reload();
    },

    printReport(reportsOption, { localOnly = false, isChangeShift = false, outstanding = null }: { localOnly?: boolean; isChangeShift?: boolean; outstanding?: any } = {}) {
      const reportStore = useReportStore();
      const {
        cash,
        income,
        incomeDetail,
        product,
        productDetail,
        voidBill,
        openMenu,
        reportProductSummary
      } = reportsOption;

      console.log("print report income");
      for (let i = 0; i < income; i++) {
        reportStore.printSummary({ isFullReport: false, actualCash: parseFloat(cash), isCloseShop: true, isLocalOnly: localOnly, isChangeShift, outstanding });
      }

      console.log("print report income detail")
      for (let i = 0; i < incomeDetail; i++) {
        reportStore.printSummary({ isFullReport: true, isCloseShop: true, isLocalOnly: localOnly, isChangeShift, outstanding })
      }

      for (let i = 0; i < product; i++) {
        reportStore.printProductSold({ isFullReport: false, reportProductSummary, isCloseShop: true, isChangeShift });
      }

      console.log("print report product detail")
      for (let i = 0; i < productDetail; i++) {
        reportStore.printProductSold({ isFullReport: true, reportProductSummary, isCloseShop: true, isChangeShift })
      }

      console.log("print report void")
      for (let i = 0; i < voidBill; i++) {
        reportStore.printVoidItems({ isCloseShop: true, isChangeShift })
      }

      console.log("print report open menu")
      for (let i = 0; i < openMenu; i++) {
        reportStore.printOpenMenu({ isCloseShop: true, isChangeShift })
      }

    },

    resetCloseStateCompleted() {
      this.closeStateCompleted = {
        refreshReceipts: false,
        syncData: false,
        sendEmailDaily: false,
        sendSmsDaily: false,
        closingBatch: false,
      }
    },

    async syncOfflineDataOrThrow() {
      const dataservice = useDataservice()
      const jobsStore = usePosJobsStore()
      const receiptsStore = usePosReceiptsStore()

      await jobsStore.syncPendingJobs()
      const pendingJobs = jobsStore.getPendingJobs()
      if (pendingJobs.length) {
        throw new Error(`Pending jobs remain: ${pendingJobs.length}`)
      }

      await receiptsStore.syncPendingReceipts()
      const pendingReceipts = receiptsStore.getPendingReceipts()
      if (pendingReceipts.length) {
        throw new Error(`Pending receipts remain: ${pendingReceipts.length}`)
      }

      await syncPendingActivities(dataservice)
      const pendingActivities = getPendingActivities()
      if (pendingActivities.length) {
        throw new Error(`Pending activities remain: ${pendingActivities.length}`)
      }
    },

    async saveReceiptsToDbAsync(receipts: any) {

    },
    async addCloseJobCard() {
      const businessDate = usePosJobsStore().getBusinessDate();
      const employee = usePosEmployeesStore().getCurrentEmployee;
      const shift = usePosJobsStore().getShift();
      // CLOSE JOB
      let closeJob = new JobBuilder()
        .withActionType(JobAction.CLOSE_SHOP)
        .withEmployee(employee)
        .withJobIdAndTimestamp(useServerTime().nowISO())
        .withCustom({
          businessDate,
          shift,
        })
        .build();

      usePosJobsStore().add(closeJob, true);
    },

    async addChangeShiftJobCard() {
      const jobsStore = usePosJobsStore();
      const businessDate = jobsStore.getBusinessDate();
      const employee = usePosEmployeesStore().getCurrentEmployee;
      const fromShift = jobsStore.getShiftNumber();
      const toShift = fromShift + 1;

      // CHANGE_SHIFT is a table-less marker job: the replay engine skips it
      // (no alias/uuid), so it only advances the shift count and never touches tables.
      const job = new JobBuilder()
        .withActionType(JobAction.CHANGE_SHIFT)
        .withEmployee(employee)
        .withJobIdAndTimestamp(useServerTime().nowISO())
        .withCustom({
          businessDate,
          fromShift,
          toShift,
        })
        .build();

      usePosJobsStore().add(job, true);
    },

    // Advance to the next shift. Unlike closeShop this is intentionally light: no
    // email/SMS/clear-data — it just (optionally) prints the closing-shift report and
    // posts a CHANGE_SHIFT job. Works offline; never blocks on connectivity.
    // `reportsOption` carries per-report copy counts chosen in the modal (income,
    // productDetail, voidBill, openMenu, ...); 0 / missing = don't print that one.
    async changeShift(reportsOption: any = {}) {
      const jobsStore = usePosJobsStore();
      const receiptsStore = usePosReceiptsStore();

      // Best-effort: pull in the closing shift's sales (incl. other terminals) before
      // printing. If offline/unreachable, fall back to local data — do NOT throw.
      let localOnly = false;
      try {
        const businessDate = jobsStore.getBusinessDate();
        await receiptsStore.flushOfflineMutations();
        await receiptsStore.refreshFromServer(businessDate, businessDate);
      } catch (e) {
        localOnly = true;
        console.warn('[changeShift] offline / refresh failed — printing local data:', e);
      }

      // Outstanding = tables still open (unpaid) at the shift change. Their grandTotal
      // carries into the next shift, so the printed Summary shows count + sum. Source
      // is the live jobchain (paid tables drop out); same computation as the modal.
      const openTables = (Object.values(jobsStore.tables || {}) as any[])
        .filter((t) => (t?.items?.length || 0) > 0);
      const outstanding = {
        count: openTables.length,
        total: openTables.reduce((sum, t) => sum + (Number(t?.billing?.grandTotal) || 0), 0),
      };

      // Print the closing-shift report(s). Reuses printReport() (same banner + ShiftWork
      // grouping) but flagged isChangeShift so the banner reads "Change Shift" (not
      // "Close Shop") and the Summary prints the outstanding line.
      const REPORT_KEYS = ['income', 'incomeDetail', 'product', 'productDetail', 'voidBill', 'openMenu'];
      const wantsPrint = REPORT_KEYS.some((k) => Number(reportsOption?.[k]) > 0);
      if (wantsPrint) {
        this.printReport({ cash: '0', ...reportsOption }, { localOnly, isChangeShift: true, outstanding });
      }

      // Post the marker job LAST: after it lands, getShift() returns the next shift and
      // every subsequently paid receipt is stamped with it.
      await this.addChangeShiftJobCard();
    },


    async sendEmailDaily() {
      const businessDate = usePosJobsStore().getBusinessDate();
      const storeName = useSettingsStore().getStoreName()
      const format = 'YYYY-MM-DD HH:mm:ss';
      const openShopTimeJob = usePosJobsStore().getFirstJob()?.timestamp || dayjs().format(format);
      const openShopTime = dayjs(openShopTimeJob).format(format);

      if (!this.data?.Id) {
        console.log("Not found customer id.")
        this.closeStateCompleted.sendEmailDaily = true
        return
      }

      try {
        const dataservice = useDataservice()
        const customerEmails = await dataservice.customerEmails.fetch()
        const toEmails = Array.isArray(customerEmails)
          ? customerEmails.map((x: any) => x.Email).filter(Boolean).join(',')
          : ''

        if (!toEmails) {
          console.log("Not found customer emails.")
          this.closeStateCompleted.sendEmailDaily = true
          return
        }

        console.log("toEmails=>", toEmails)
        const payload = {
          "startDate": businessDate,
          "endDate": businessDate,
          "customerId": this.data?.Id,
          "shopName": storeName,
          "openShop": openShopTime,
          "timestamp": dayjs().format('YYYY-MM-DD HH:mm:ss'),
          "to": toEmails
        }

        await dataservice.emailDaily.post(payload)
      } catch (e) {
        console.error("sendEmailDaily failed, continuing close-shop flow:", e)
      }
      this.closeStateCompleted.sendEmailDaily = true
    },

    async sendSmsDaily() {
      const businessDate = usePosJobsStore().getBusinessDate();
      const shopName = useSettingsStore().getStoreName()

      if (!this.data?.Id) {
        console.log("Not found customer id.")
        this.closeStateCompleted.sendSmsDaily = true
        return
      }

      const smsReport = useSettingsStore().getSystemEnvByName("SmsReport")
      if (!smsReport || !smsReport.Value) {
        console.log("Not found SmsReport setting, skip SMS.")
        this.closeStateCompleted.sendSmsDaily = true
        return
      }

      try {
        const payload = {
          startDate: businessDate,
          endDate: businessDate,
          shopName,
        }
        await useDataservice().sms.post(payload)
      } catch (e) {
        console.error("sendSmsDaily failed, continuing close-shop flow:", e)
      }
      this.closeStateCompleted.sendSmsDaily = true
    },

    async updateCloseSummary() {
      const businessModeRaw = useSettingsStore().getSystemEnvByName("BusinessMode")?.Value || "0";
      const businessMode = Number(businessModeRaw) || 0;
      const now = dayjs();
      const summaryBusinessDate = usePosJobsStore().getBusinessDate() || now.format('YYYY-MM-DD');

      // Per-day summary doc (existing): record close timestamp under businessDate
      const summaryCollectionRef = useFirestore().getSummaryCollectionRef()
      const liveDocRef = doc(summaryCollectionRef, summaryBusinessDate)
      await setDoc(liveDocRef, {
        closed: now.format('YYYY-MM-DDTHH:mm:ss'),
      }, { merge: true })

      // customers/{storeId} root doc: switch to closed state
      try {
        const rootRef = useFirestore().getRootDataRef();
        await setDoc(
          rootRef,
          {
            status: "closed",
            closed: now.toISOString(),
            opened: deleteField(),
            businessMode,
            live: {
              businessMode,
              businessDate: summaryBusinessDate,
            },
          },
          { merge: true },
        );
      } catch (e) {
        console.error("closeShop: failed to update customer doc", e);
      }

      return
    },

    async clearCloudData() {

      const jobRef = useRealtimeDatabase().jobchainRef()
      await set(jobRef, null)

      //tables 
      const tablesRef = useRealtimeDatabase().tablesRef()
      await set(tablesRef, null)

      const tableStateRef = useRealtimeDatabase().tablesStateRef()
      await set(tableStateRef, null)

      const tablesV2Ref = useRealtimeDatabase().tablesV2Ref()
      await set(tablesV2Ref, null)

      const tablesV3Ref = useRealtimeDatabase().tablesV3Ref()
      await set(tablesV3Ref, null)

      const tablesV2HistoryRef_OLD = useRealtimeDatabase().tablesV2HistoryRef_OLD()
      await set(tablesV2HistoryRef_OLD, null)

      // alias-keyed per-table history for the phone's History tab
      const mobileV2HistoryRef = useRealtimeDatabase().mobileV2HistoryRef()
      await set(mobileV2HistoryRef, null)


      //biill
      const billNumberRef = useRealtimeDatabase().billNumberRef()
      await set(billNumberRef, null)

      const billNumberRef_OLD = useRealtimeDatabase().billNumberRef_OLD()
      await set(billNumberRef_OLD, null)

      // legacy JOB silo — drain the mobile/handshake nodes for the new day
      // (the Job # is computed live from the jobchain, so there is no counter)
      const jobMobileRef = useRealtimeDatabase().jobMobileRef()
      await set(jobMobileRef, null)
      const jobHandshakeRef = useRealtimeDatabase().jobHandshakeRef()
      await set(jobHandshakeRef, null)


      // Wipe the per-store IDB runtime mirrors so the next business day
      // boots clean (no stale receipts/jobs/tableStates/soldout+splitBill).
      const sid = this.storeId
      if (sid) {
        try {
          await clearOfflineRuntime(String(sid))
        } catch (err) {
          console.warn("[closeShop] clearOfflineRuntime failed", err)
        }
      }
    },


  },
});
